import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, Badge } from '@tremor/react';
import { Banknote, TrendingUp, TrendingDown, ShoppingCart, Ticket } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';
import {
  formatRangeShort,
  useDateRange,
  useDateRangeKey,
} from '@/lib/dateRange';

interface SummaryResponse {
  as_of: string;
  period_days: number;
  revenue_brl: number;
  previous_revenue_brl: number;
  growth_pct_vs_prev: number | null;
  purchases: number;
  paying_orgs: number;
  total_credits_sold: number;
  arppu_brl: number | null;
  refunds: { count: number; credits_returned: number };
}

interface RevenueResponse {
  buckets: Array<{
    bucket: string;
    revenue_brl: number;
    purchases: number;
    paying_orgs: number;
  }>;
}

interface PackagesResponse {
  packages: Array<{
    key: string;
    name: string;
    package_credits: number;
    package_price_brl: number;
    purchases: number;
    credits_sold: number;
    revenue_brl: number;
  }>;
}

interface TransactionsResponse {
  items: Array<{
    id: number;
    org_id: number | null;
    org_name: string | null;
    type: string;
    amount: number;
    balance_after: number;
    description: string | null;
    package_key: string | null;
    package_price_brl: number | null;
    stripe_payment_intent_id: string | null;
    created_at: string | null;
  }>;
  total: number;
}

/** GET /monetization/cupons — fonte do cupom é o Stripe; org/pacote vêm do banco. */
interface CuponsResponse {
  codigos: Array<{
    tipo: string;
    code: string | null;
    promotion_code_id: string | null;
    ativo: boolean;
    coupon_id: string | null;
    coupon_nome: string | null;
    percent_off: number | null;
    amount_off: number | null;
    currency: string | null;
    duration: string | null;
    duration_in_months: number | null;
    times_redeemed_total: number;
    max_redemptions: number | null;
    expires_at: string | null;
    resgates_periodo: number;
    desconto_periodo_centavos: number;
    receita_periodo_centavos: number;
    ultimo_uso: string | null;
  }>;
  usos: Array<{
    session_id: string | null;
    created: string | null;
    email: string | null;
    amount_subtotal: number;
    amount_discount: number;
    amount_total: number;
    code: string | null;
    coupon_id: string | null;
    org_id: number | null;
    org_name: string | null;
    package_key: string | null;
  }>;
  totais: {
    sessoes_pagas: number;
    sessoes_pagas_com_cupom: number;
    sessoes_pagas_sem_cupom: number;
    pct_com_cupom: number | null;
    desconto_total_centavos: number;
    receita_com_cupom_centavos: number;
    codigos_ativos: number;
    codigos_total: number;
  };
  sessoes_varridas?: number;
  cache?: boolean;
  generated_at?: string;
}

const MAX_USOS = 200;

const fmtBrl = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
const fmtInt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : new Intl.NumberFormat('pt-BR').format(n);
/** Centavos (como o Stripe devolve) → R$. */
const fmtCent = (c: number | null | undefined) =>
  c === null || c === undefined ? '—' : fmtBrl(c / 100);
const fmtData = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
const fmtDataHora = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('pt-BR') : '—';

function descontoLabel(c: CuponsResponse['codigos'][number]): string {
  if (c.percent_off !== null && c.percent_off !== undefined) {
    return `${c.percent_off}%`;
  }
  if (c.amount_off !== null && c.amount_off !== undefined) {
    const valor = fmtCent(c.amount_off);
    return (c.currency ?? 'brl').toLowerCase() === 'brl'
      ? valor
      : `${c.amount_off / 100} ${(c.currency ?? '').toUpperCase()}`;
  }
  return '—';
}

function duracaoLabel(c: CuponsResponse['codigos'][number]): string {
  switch (c.duration) {
    case 'forever':
      return 'Para sempre';
    case 'once':
      return 'Uma vez';
    case 'repeating':
      return c.duration_in_months
        ? `${c.duration_in_months} meses`
        : 'Recorrente';
    default:
      return c.duration ?? '—';
  }
}

export function MonetizationPage() {
  const { range, apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();

  const summary = useQuery<SummaryResponse>({
    queryKey: ['monetization.summary', rangeKey],
    queryFn: async () =>
      (await api().get('/monetization/summary', { params: apiParams })).data,
  });
  const revenue = useQuery<RevenueResponse>({
    queryKey: ['monetization.revenue', rangeKey],
    queryFn: async () =>
      (
        await api().get('/monetization/revenue', {
          params: { ...apiParams, bucket: 'day' },
        })
      ).data,
  });
  const packages = useQuery<PackagesResponse>({
    queryKey: ['monetization.packages', rangeKey],
    queryFn: async () =>
      (await api().get('/monetization/packages', { params: apiParams })).data,
  });
  const transactions = useQuery<TransactionsResponse>({
    queryKey: ['monetization.transactions', rangeKey],
    queryFn: async () =>
      (
        await api().get('/monetization/transactions', {
          params: { ...apiParams, per_page: 20 },
        })
      ).data,
  });

  // Cupons: vem do Stripe via script do app principal (pode demorar/falhar
  // sozinho). Query isolada + `retry: false` para o erro não derrubar a página.
  const cupons = useQuery<CuponsResponse>({
    queryKey: ['monetization.cupons', rangeKey],
    queryFn: async () =>
      (await api().get('/monetization/cupons', { params: apiParams })).data,
    retry: false,
  });
  const cuponsErro = cupons.error
    ? extractErrorMessage(cupons.error, 'Falha ao consultar os cupons no Stripe.')
    : null;
  const codigos = cupons.data?.codigos ?? [];
  const usos = cupons.data?.usos ?? [];
  const totCupons = cupons.data?.totais;

  const series = revenue.data?.buckets ?? [];
  const peak = Math.max(1, ...series.map((b) => b.revenue_brl));

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Title>Monetization</Title>
        <Text>
          Receita, MRR, ARPPU, vendas por pacote · {formatRangeShort(range)}.
        </Text>
      </header>

      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <KPICard
          label={`Receita · ${formatRangeShort(range)}`}
          value={summary.isLoading ? null : fmtBrl(summary.data?.revenue_brl)}
          loading={summary.isLoading}
          error={summary.error ? extractErrorMessage(summary.error, 'erro') : null}
          delta={
            summary.data?.growth_pct_vs_prev !== null &&
            summary.data?.growth_pct_vs_prev !== undefined
              ? summary.data.growth_pct_vs_prev / 100
              : null
          }
          hint={`vs. ${fmtBrl(summary.data?.previous_revenue_brl)} no período anterior`}
        />
        <KPICard
          label="Compras"
          value={summary.isLoading ? null : fmtInt(summary.data?.purchases)}
          loading={summary.isLoading}
          hint="Transações no período"
        />
        <KPICard
          label="Orgs pagantes"
          value={summary.isLoading ? null : fmtInt(summary.data?.paying_orgs)}
          loading={summary.isLoading}
          hint={`ARPPU: ${fmtBrl(summary.data?.arppu_brl)}`}
        />
        <KPICard
          label="Créditos vendidos"
          value={summary.isLoading ? null : fmtInt(summary.data?.total_credits_sold)}
          loading={summary.isLoading}
          hint={
            summary.data?.refunds && summary.data.refunds.count > 0
              ? `${summary.data.refunds.count} cortesias/estornos (${summary.data.refunds.credits_returned} créditos)`
              : 'Sem estornos'
          }
        />
      </Grid>

      <Card>
        <div className="flex items-center justify-between">
          <Title className="text-base">
            Receita diária · {formatRangeShort(range)}
          </Title>
          {summary.data && (
            <Badge color={summary.data.growth_pct_vs_prev && summary.data.growth_pct_vs_prev > 0 ? 'emerald' : 'red'} icon={summary.data.growth_pct_vs_prev && summary.data.growth_pct_vs_prev > 0 ? TrendingUp : TrendingDown}>
              {summary.data.growth_pct_vs_prev !== null
                ? `${summary.data.growth_pct_vs_prev > 0 ? '+' : ''}${summary.data.growth_pct_vs_prev.toFixed(1)}%`
                : '—'}
            </Badge>
          )}
        </div>
        {revenue.isLoading && <LoadingSkeleton rows={3} />}
        {!revenue.isLoading && series.length === 0 && (
          <Text className="text-slate-400">Sem vendas no período.</Text>
        )}
        {!revenue.isLoading && series.length > 0 && (
          <div className="mt-3 flex h-32 items-end gap-0.5">
            {series.map((b) => (
              <div
                key={b.bucket}
                title={`${new Date(b.bucket).toLocaleDateString('pt-BR')}: ${fmtBrl(b.revenue_brl)} (${b.purchases} compras)`}
                className="flex-1 rounded-sm bg-emerald-500/60 transition hover:bg-emerald-400"
                style={{ height: `${Math.max(2, (b.revenue_brl / peak) * 100)}%` }}
              />
            ))}
          </div>
        )}
      </Card>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <div className="flex items-center gap-2">
            <Banknote className="h-4 w-4 text-emerald-300" />
            <Title className="text-base">
              Vendas por pacote · {formatRangeShort(range)}
            </Title>
          </div>
          {packages.isLoading && <LoadingSkeleton rows={4} />}
          {!packages.isLoading && (
            <table className="mt-3 w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs text-slate-400">
                  <th className="py-2">Pacote</th>
                  <th>Compras</th>
                  <th>Créditos</th>
                  <th>Receita</th>
                </tr>
              </thead>
              <tbody>
                {packages.data?.packages.map((p) => (
                  <tr key={p.key} className="border-b border-slate-800/50">
                    <td className="py-2">
                      <div className="text-slate-200">{p.name}</div>
                      <div className="text-xs text-slate-500">
                        {fmtBrl(p.package_price_brl)} · {p.package_credits} créditos
                      </div>
                    </td>
                    <td>{fmtInt(p.purchases)}</td>
                    <td>{fmtInt(p.credits_sold)}</td>
                    <td className="font-medium text-emerald-300">{fmtBrl(p.revenue_brl)}</td>
                  </tr>
                ))}
                {packages.data?.packages.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-slate-400">
                      Sem vendas no período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-blue-300" />
            <Title className="text-base">Transações recentes</Title>
          </div>
          {transactions.isLoading && <LoadingSkeleton rows={5} />}
          {!transactions.isLoading && (
            <div className="mt-3 max-h-96 space-y-1 overflow-y-auto">
              {transactions.data?.items.map((t) => (
                <div
                  key={t.id}
                  className="rounded border border-slate-800 px-3 py-2 text-xs"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-200">
                      {t.org_name ?? `org #${t.org_id}`}
                    </span>
                    <Badge
                      color={t.type === 'purchase' ? 'emerald' : 'slate'}
                      size="xs"
                    >
                      {t.type}
                    </Badge>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between text-slate-400">
                    <span className="truncate">{t.description ?? t.package_key ?? '—'}</span>
                    <span className="font-mono">
                      {t.amount > 0 ? '+' : ''}
                      {t.amount} cred · {fmtBrl(t.package_price_brl)}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[10px] text-slate-600">
                    {t.created_at ? new Date(t.created_at).toLocaleString('pt-BR') : '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </Grid>

      {/* ───────────────────────── Cupons Stripe ───────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Ticket className="h-4 w-4 text-amber-300" />
          <Title className="text-base">
            Cupons Stripe · {formatRangeShort(range)}
          </Title>
        </div>
        <Text className="text-xs text-slate-500">
          Códigos vêm do Stripe (ativos e inativos, mesmo sem uso). Org e pacote
          vêm do banco pelo id da sessão de checkout.
          {cupons.data?.generated_at
            ? ` Consulta de ${fmtDataHora(cupons.data.generated_at)}${cupons.data.cache ? ' (cache)' : ''}.`
            : ''}
        </Text>

        {cuponsErro && (
          <Card>
            <div className="text-sm text-red-400" role="alert">
              {cuponsErro}
            </div>
            <Text className="mt-1 text-xs text-slate-500">
              O resto da página não depende dos cupons.
            </Text>
          </Card>
        )}

        {!cuponsErro && (
          <>
            <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
              <KPICard
                label="Compras com cupom"
                value={
                  cupons.isLoading
                    ? null
                    : fmtInt(totCupons?.sessoes_pagas_com_cupom)
                }
                loading={cupons.isLoading}
                hint={
                  totCupons
                    ? `${totCupons.pct_com_cupom !== null ? `${totCupons.pct_com_cupom.toFixed(1)}%` : '—'} das ${fmtInt(totCupons.sessoes_pagas)} compras pagas`
                    : 'Sessões de checkout pagas com desconto'
                }
              />
              <KPICard
                label="Desconto concedido"
                value={
                  cupons.isLoading
                    ? null
                    : fmtCent(totCupons?.desconto_total_centavos)
                }
                loading={cupons.isLoading}
                hint="Soma dos descontos no período"
              />
              <KPICard
                label="Receita com cupom"
                value={
                  cupons.isLoading
                    ? null
                    : fmtCent(totCupons?.receita_com_cupom_centavos)
                }
                loading={cupons.isLoading}
                hint="Valor efetivamente pago nessas compras"
              />
              <KPICard
                label="Códigos ativos"
                value={cupons.isLoading ? null : fmtInt(totCupons?.codigos_ativos)}
                loading={cupons.isLoading}
                hint={
                  totCupons
                    ? `${fmtInt(totCupons.codigos_total)} códigos no Stripe`
                    : 'Promotion codes + cupons'
                }
              />
            </Grid>

            <Card>
              <Title className="text-base">Códigos</Title>
              {cupons.isLoading && <LoadingSkeleton rows={4} />}
              {!cupons.isLoading && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full min-w-[980px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-800 text-left text-xs text-slate-400">
                        <th className="py-2">Código</th>
                        <th>Cupom</th>
                        <th>Desconto</th>
                        <th>Duração</th>
                        <th>Ativo</th>
                        <th className="text-right">Resgates no período</th>
                        <th className="text-right">Desconto no período</th>
                        <th className="text-right">Receita no período</th>
                        <th className="text-right">Resgates totais</th>
                        <th>Teto</th>
                        <th>Validade</th>
                        <th>Último uso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codigos.map((c) => (
                        <tr
                          key={c.promotion_code_id ?? c.coupon_id ?? c.code}
                          className="border-b border-slate-800/50"
                        >
                          <td className="py-2 font-mono text-slate-200">
                            {c.code ?? '—'}
                          </td>
                          <td>
                            <div className="text-slate-300">
                              {c.coupon_nome ?? c.coupon_id ?? '—'}
                            </div>
                            {c.coupon_id && (
                              <div className="text-xs text-slate-500">
                                {c.coupon_id}
                                {c.tipo === 'coupon' ? ' · sem promotion code' : ''}
                                {c.tipo === 'desconhecido' ? ' · fora do Stripe hoje' : ''}
                              </div>
                            )}
                          </td>
                          <td>{descontoLabel(c)}</td>
                          <td>{duracaoLabel(c)}</td>
                          <td>
                            <Badge color={c.ativo ? 'emerald' : 'slate'} size="xs">
                              {c.ativo ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </td>
                          <td className="text-right font-medium text-slate-200">
                            {fmtInt(c.resgates_periodo)}
                          </td>
                          <td className="text-right text-amber-300">
                            {fmtCent(c.desconto_periodo_centavos)}
                          </td>
                          <td className="text-right text-emerald-300">
                            {fmtCent(c.receita_periodo_centavos)}
                          </td>
                          <td className="text-right">{fmtInt(c.times_redeemed_total)}</td>
                          <td>
                            {c.max_redemptions !== null && c.max_redemptions !== undefined
                              ? fmtInt(c.max_redemptions)
                              : 'sem teto'}
                          </td>
                          <td>{c.expires_at ? fmtData(c.expires_at) : 'sem validade'}</td>
                          <td className="text-xs text-slate-400">
                            {c.ultimo_uso ? fmtDataHora(c.ultimo_uso) : '—'}
                          </td>
                        </tr>
                      ))}
                      {codigos.length === 0 && (
                        <tr>
                          <td colSpan={12} className="py-4 text-center text-slate-400">
                            Nenhum cupom cadastrado no Stripe.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card>
              <Title className="text-base">
                Usos no período · {formatRangeShort(range)}
              </Title>
              {cupons.isLoading && <LoadingSkeleton rows={4} />}
              {!cupons.isLoading && (
                <>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full min-w-[760px] text-sm">
                      <thead>
                        <tr className="border-b border-slate-800 text-left text-xs text-slate-400">
                          <th className="py-2">Data</th>
                          <th>Org / e-mail</th>
                          <th>Código</th>
                          <th>Pacote</th>
                          <th className="text-right">Subtotal</th>
                          <th className="text-right">Desconto</th>
                          <th className="text-right">Pago</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usos.slice(0, MAX_USOS).map((u) => (
                          <tr
                            key={u.session_id ?? `${u.created}-${u.email}`}
                            className="border-b border-slate-800/50"
                          >
                            <td className="py-2 text-xs text-slate-400">
                              {fmtDataHora(u.created)}
                            </td>
                            <td>
                              <div className="text-slate-200">
                                {u.org_name ?? (u.org_id ? `org #${u.org_id}` : '—')}
                              </div>
                              <div className="text-xs text-slate-500">
                                {u.email ?? '—'}
                              </div>
                            </td>
                            <td className="font-mono text-slate-300">
                              {u.code ?? u.coupon_id ?? '—'}
                            </td>
                            <td>{u.package_key ?? '—'}</td>
                            <td className="text-right">{fmtCent(u.amount_subtotal)}</td>
                            <td className="text-right text-amber-300">
                              −{fmtCent(u.amount_discount)}
                            </td>
                            <td className="text-right font-medium text-emerald-300">
                              {fmtCent(u.amount_total)}
                            </td>
                          </tr>
                        ))}
                        {usos.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-4 text-center text-slate-400">
                              Nenhuma compra com cupom no período.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {usos.length > MAX_USOS && (
                    <Text className="mt-2 text-xs text-amber-300">
                      Mostrando os {MAX_USOS} usos mais recentes de{' '}
                      {fmtInt(usos.length)} no período.
                    </Text>
                  )}
                </>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
